// react-routers components
// import { Link } from "react-router-dom";

// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// React
import { useEffect, useMemo, useState } from "react";
// @mui material components
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import { Edit, Save } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
// import ChemicalTable from "examples/Tables/ChemicalTable";

// firebase functions
import {
  // getDoc,
  doc,
  setDoc,
  // Timestamp,
  // setDoc,
  // doc,
} from "firebase/firestore";
import { TableContainer } from "@mui/material";
import { db } from "../../../firebaseDb";

const perTank = (amount, measure, hectares, unit, waterRate, tankCapacity) => {
  const tanksperHectare = waterRate / tankCapacity;
  if (measure === "Tank") {
    return `${amount} ${unit}`;
  }
  if (measure === "Hectare") {
    return `${Math.round((100 * Number(amount)) / tanksperHectare) / 100} ${unit}`;
  }
  if (measure === "1000 L") {
    return `${Math.round((100 * (Number(amount) * tankCapacity)) / 1000) / 100} ${unit}`;
  }
  return null;
};

const perPartTank = (amount, measure, hectares, unit, waterRate, tankCapacity) => {
  const tanksperHectare = waterRate / tankCapacity;
  const fraction =
    (Number(hectares) * Number(waterRate) -
      Math.floor((Number(hectares) * Number(waterRate)) / tankCapacity) * tankCapacity) /
    tankCapacity;

  if (measure === "Tank") {
    return `${Math.round(100 * amount * fraction) / 100} ${unit}`;
  }
  if (measure === "Hectare") {
    return `${Math.round((100 * (Number(amount) * fraction)) / tanksperHectare) / 100} ${unit}`;
  }
  if (measure === "1000 L") {
    return `${Math.round((100 * (Number(amount) * tankCapacity * fraction)) / 1000) / 100} ${unit}`;
  }
  return null;
};

const totalChemical = (amount, measure, hectares, unit, waterRate, tankCapacity) => {
  const tanksperHectare = waterRate / tankCapacity;
  if (measure === "Tank") {
    return Math.round(100 * amount * tanksperHectare * hectares) / 100;
  }
  if (measure === "Hectare") {
    return `${Math.round(100 * Number(amount) * hectares) / 100} ${unit}`;
  }
  if (measure === "1000 L") {
    return `${Math.round((100 * Number(amount) * waterRate * hectares) / 1000) / 100} ${unit}`;
  }
  return null;
};

function ChemicalsList({ title, sprayInfo, shadow, hectares, chemicalsData }) {
  const [chemicalList, setChemicals] = useState(sprayInfo.chemicals || []);
  const [loading, setLoading] = useState(false);
  const [selectedrow, setSelectedRow] = useState("");
  const [ppe, setPPE] = useState(sprayInfo.PPE || []);

  const sdsUrls = useMemo(() => {
    const urlsByKey = {};
    const urlsByName = {};
    (chemicalsData || []).forEach((chemical) => {
      if (!chemical) return;
      if (chemical.key && chemical.sdsUrl) urlsByKey[chemical.key] = chemical.sdsUrl;
      if (chemical.chemicalName && chemical.sdsUrl)
        urlsByName[chemical.chemicalName] = chemical.sdsUrl;
    });
    return { urlsByKey, urlsByName };
  }, [chemicalsData]);

  useEffect(() => {
    setChemicals(sprayInfo.chemicals || []);
  }, [sprayInfo.chemicals]);

  useEffect(() => {
    setPPE(sprayInfo.PPE || []);
  }, [sprayInfo.PPE]);

  const getChemicalSdsUrl = (row) => {
    const candidate = sdsUrls.urlsByKey[row.key] || sdsUrls.urlsByName[row.chemicalName];
    if (!candidate) return "";
    try {
      const parsedUrl = new URL(candidate);
      if (parsedUrl.protocol === "https:") return candidate;
    } catch (_error) {
      return "";
    }
    return "";
  };

  const setBatch = (value, index) => {
    setChemicals((prevArray) => {
      const newArray = [...prevArray];
      newArray[index].batchNo = value;
      return newArray;
    });
  };

  const setActualChemUsed = (value, index) => {
    setChemicals((prevArray) => {
      const newArray = [...prevArray];
      newArray[index].actualChemical = value;
      return newArray;
    });
  };

  const saveChemicals = (index, key) => {
    const prevChem = chemicalList;
    prevChem[index].disabled = true;
    setSelectedRow(index);
    setLoading(true);
    setChemicals((prevArray) => {
      const newArray = [...prevArray];
      newArray[index].disabled = true;
      return newArray;
    });
    const chemRef = doc(db, "Sprays", key);
    setDoc(chemRef, { chemicals: prevChem }, { merge: true }).then(() => {
      setLoading(false);
    });
  };

  const editChemicals = (index) => {
    setChemicals((prevArray) => {
      const newArray = [...prevArray];
      newArray[index].disabled = false;
      return newArray;
    });
  };

  const handlePPEToggle = (option) => {
    setPPE((prevPPE) => {
      if (prevPPE.includes(option)) {
        return prevPPE.filter((item) => item !== option); // Remove if already selected
      }
      return [...prevPPE, option]; // Add if not selected
    });
  };

  const savePPE = () => {
    setLoading(true);
    const ppeRef = doc(db, "Sprays", sprayInfo.key);
    setDoc(ppeRef, { PPE: ppe }, { merge: true }).then(() => {
      setLoading(false);
    });
  };

  return (
    <Card sx={{ height: "100%", boxShadow: !shadow && "none" }}>
      <MDBox pt={2} px={2}>
        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          {`${title} (Click on a chemical name to view its SDS)`}
        </MDTypography>
      </MDBox>
      <TableContainer>
        <Table aria-label="simple table">
          <TableRow component="th">
            <TableCell sx={{ fontWeight: "bold" }} align="left">
              Chemical Name
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Spray Rate
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Per Full Tank
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Per Part Tank
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Total Chemical Used
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Actual Chemical Used
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Batch Number
            </TableCell>
          </TableRow>
          <TableBody>
            {chemicalList.map((row, index) => {
              const rowSdsUrl = getChemicalSdsUrl(row);
              return (
                <TableRow
                  hover
                  key={row.key}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell align="left">
                    {rowSdsUrl ? (
                      <a
                        href={rowSdsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "blue", textDecoration: "bold" }}
                      >
                        {row.chemicalName}
                      </a>
                    ) : (
                      row.chemicalName
                    )}
                  </TableCell>
                  <TableCell align="center">{`${row.sprayRate} ${row.unit} / ${row.measure}`}</TableCell>
                  <TableCell align="center">
                    {perTank(
                      row.sprayRate,
                      row.measure,
                      hectares,
                      row.unit,
                      sprayInfo.waterRate,
                      sprayInfo.tankCapacity
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {perPartTank(
                      row.sprayRate,
                      row.measure,
                      hectares,
                      row.unit,
                      sprayInfo.waterRate,
                      sprayInfo.tankCapacity
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {totalChemical(
                      row.sprayRate,
                      row.measure,
                      hectares,
                      row.unit,
                      sprayInfo.waterRate,
                      sprayInfo.tankCapacity
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      disabled={row.disabled}
                      value={row.actualChemical}
                      label="Actual Chemical"
                      sx={{ fontSize: "1rem" }}
                      onChange={(event) => setActualChemUsed(event.target.value, index)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      InputProps={{
                        style: {
                          backgroundColor: row.disabled ? "lightgrey" : "lightgreen",
                        },
                      }}
                      value={row.batchNo}
                      disabled={row.disabled}
                      onChange={(event) => setBatch(event.target.value, index)}
                    />
                  </TableCell>
                  {loading && index === selectedrow && (
                    <TableCell align="center">
                      <CircularProgress />
                    </TableCell>
                  )}
                  {!loading && (
                    <TableCell align="center">
                      {!row.disabled && (
                        <MDButton
                          variant="contained"
                          color="error"
                          onClick={() => {
                            saveChemicals(index, sprayInfo.key);
                          }}
                        >
                          <Save />
                        </MDButton>
                      )}
                      {row.disabled && (
                        <MDButton
                          variant="contained"
                          color="error"
                          onClick={() => {
                            editChemicals(index);
                          }}
                        >
                          <Edit />
                        </MDButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <MDBox pt={2} px={2} pb={2}>
        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          Select PPE
        </MDTypography>
        <FormGroup row>
          {["Full PPE", "Spray Suit", "Gloves", "Goggles/Face Shield", "Respirator"].map(
            (option) => (
              <FormControlLabel
                key={option}
                control={
                  <Checkbox
                    checked={ppe.includes(option)}
                    onChange={() => handlePPEToggle(option)}
                  />
                }
                label={option}
              />
            )
          )}
        </FormGroup>
        <MDButton
          variant="contained"
          color="primary"
          onClick={savePPE}
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Save PPE"}
        </MDButton>
      </MDBox>
    </Card>
  );
}

// Setting default props for the ProfilesList
ChemicalsList.defaultProps = {
  shadow: true,
  chemicalsData: [],
};

// Typechecking props for the ProfilesList
ChemicalsList.propTypes = {
  title: PropTypes.string.isRequired,
  shadow: PropTypes.bool,
  sprayInfo: PropTypes.shape({
    key: PropTypes.string,
    chemicals: PropTypes.arrayOf(PropTypes.object),
    PPE: PropTypes.arrayOf(PropTypes.string),
    waterRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tankCapacity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  hectares: PropTypes.number.isRequired,
  chemicalsData: PropTypes.arrayOf(PropTypes.object),
};

export default ChemicalsList;
